import { Menu } from 'components/Menu/Menu'

import { Item, Section } from '@react-stately/collections'

import { ItemElement, SectionElement } from '@react-types/shared'
import {
  // AddTask,
  BorderStyle,
  CellMerge,
  ClearFormatting,
  ColumnLeft,
  ColumnRight,
  // Comment,
  Copy,
  Delete,
  Disapprove,
  // DistributeColumns,
  FitToDoc,
  Insert,
  RowAbove,
  RowBelow,
  // Sort,
  // SortOrderAtoZ,
  // SortOrderZtoA,
  TableAlignmentTop,
  TableBorder,
} from 'almanac-ui/icons'
import BorderThickness from 'almanac-ui/icons/16/BorderThickness'
import { TextColor } from 'almanac-ui/icons/16/TextColor'
import {
  _deleteCellContents,
  addColumnAfter,
  addColumnBefore,
  addRowAfter,
  addRowBefore,
  distributeColumns,
  mergeCells,
} from 'common/Prose/Commands/table/mutation'
import { CellColorIcon } from 'common/Prose/Plugins/FormatBars/components/TableFormatBar/components/CellColorIcon/CellColorIcon'
import { canMergeCells } from 'common/Prose/Plugins/tableSelection/tableSelectionUtils'
import { useClearFormattingButton } from 'common/Prose/Toolbar/hooks/useClearFormattingButton'
import { isTableSelection } from 'common/Prose/utils/table'
import { CellSelection } from 'common/Prose/utils/table/CellSelection'
import { TableMap } from 'common/Prose/utils/table/TableMap'
import { computed } from 'mobx'
import { NodeSelection } from 'prosemirror-state'
import { EditorView } from 'prosemirror-view'
import React, { useContext, useLayoutEffect } from 'react'
import { AlmanacEditorStateContext } from 'stores/EditorState'
import { ActiveColorsIndicator } from '../ActiveColorsIndicator/ActiveColorsIndicator'
import { TableBorderColorPicker } from '../TableBorderColorPicker/TableBorderColorPicker'
import { TableBorderStylePicker } from '../TableBorderStylePicker/TableBorderStylePicker'
import { TableBorderThicknessPicker } from '../TableBorderThicknessPicker/TableBorderThicknessPicker'
import { TableCellColorPicker } from '../TableCellColorPicker/TableCellColorPicker'
import { TextColorPicker } from '../TextColorPicker/TextColorPicker'
import { useVerticalAlignmentPicker2 } from '../VerticalAlignmentPicker2/VerticalAlignmentPicker'
import Styles from './TableContextMenu.module.scss'

const enum ItemKey {
  FIT_TO_PAGE_WIDTH = 'FIT_TO_PAGE_WIDTH',
  MERGE_CELLS = 'MERGE_CELLS',
  INSERT = 'INSERT',
  INSERT_COL_LEFT = 'INSERT_COL_LEFT',
  INSERT_COL_RIGHT = 'INSERT_COL_RIGHT',
  INSERT_ROW_BELOW = 'INSERT_ROW_BELOW',
  INSERT_ROW_ABOVE = 'INSERT_ROW_ABOVE',
  CLEAR_FORMATTING = 'CLEAR_FORMATTING',
  CLEAR_CONTENTS = 'CLEAR_CONTENTS',
  DUPLICATE = 'DUPLICATE',
  DELETE = 'DELETE',
}

function BorderOptions({ view }: { view: EditorView }): React.ReactElement {
  return (
    <div>
      <div className={Styles.borderHeading}>
        <TableBorder />
        Border color
      </div>
      <TableBorderColorPicker includeDropdownStyles={false} view={view} focusView={false} />
      <div className={Styles.borderHeading}>
        <BorderThickness />
        Border thickness
      </div>
      <TableBorderThicknessPicker includeDropdownStyles={false} view={view} focusView={false} />
      <div className={Styles.borderHeading}>
        <BorderStyle /> Border style
      </div>
      <TableBorderStylePicker includeDropdownStyles={false} view={view} focusView={false} />
    </div>
  )
}

export function useTableContextMenu(view: EditorView, trackingChanges: boolean, onClose?: () => void) {
  const editorState = useContext(AlmanacEditorStateContext)

  const activeColors = computed(() => Array.from(editorState?.activeColors ?? [])).get()

  const alignment = useVerticalAlignmentPicker2({ view })

  const clearFormatting = useClearFormattingButton(view)

  const cellSelection = view.state.selection instanceof CellSelection
  const tableSelection = isTableSelection(view.state.selection)

  useLayoutEffect(() => {
    if (!cellSelection && !tableSelection) onClose?.()
  }, [cellSelection, tableSelection, onClose])

  if (!cellSelection && !tableSelection) return <></>

  const onAction = (_key: React.Key) => {
    if (typeof _key !== 'string') return
    const key = _key as ItemKey

    switch (key) {
      case ItemKey.FIT_TO_PAGE_WIDTH: {
        const tablePos = view.state.selection.from
        const tableEl = view.nodeDOM(tablePos) as HTMLElement | null
        if (tableEl != null) {
          distributeColumns(tableEl.offsetWidth, tablePos)(view.state, view.dispatch)
        }
        return
      }

      case ItemKey.MERGE_CELLS: {
        mergeCells(view.state, view.dispatch)
        return
      }

      case ItemKey.INSERT_COL_LEFT: {
        addColumnBefore(view.state, view.dispatch)
        return
      }
      case ItemKey.INSERT_COL_RIGHT: {
        addColumnAfter(view.state, view.dispatch)
        return
      }
      case ItemKey.INSERT_ROW_BELOW: {
        addRowAfter(view.state, view.dispatch)
        return
      }
      case ItemKey.INSERT_ROW_ABOVE: {
        addRowBefore(view.state, view.dispatch)
        return
      }

      case ItemKey.CLEAR_FORMATTING: {
        clearFormatting()
        return
      }

      case ItemKey.CLEAR_CONTENTS: {
        const selection = view.state.selection
        let tablePos = selection.$from.pos
        let tableNode = selection.$from.nodeAfter
        if (tableNode == null || tableNode.type !== view.state.schema.nodes.table) {
          return
        }

        for (let row = 0; ; ++row) {
          const tableMap = new TableMap({ posBeforeTable: tablePos, node: tableNode })
          if (row >= tableMap.numRows) break
          const cells = tableMap.getOrderedPosListInRow(row).map((pos) => view.state.doc.resolve(pos))
          const cellSelection = new CellSelection(cells[0], cells[cells.length - 1], cells)

          const trSelect = view.state.tr.setSelection(cellSelection)
          view.dispatch(trSelect)
          const trDelete = _deleteCellContents(view.state)
          view.dispatch(trDelete)

          tablePos = trDelete.mapping.map(trSelect.mapping.map(tablePos))
          tableNode = view.state.doc.resolve(tablePos).nodeAfter
          if (tableNode == null || tableNode.type !== view.state.schema.nodes.table) {
            return
          }
        }

        view.dispatch(view.state.tr.setSelection(new NodeSelection(view.state.doc.resolve(tablePos))))
        return
      }

      case ItemKey.DUPLICATE: {
        const selection = view.state.selection
        let tableNode = selection.$from.nodeAfter
        if (tableNode == null || tableNode.type !== view.state.schema.nodes.table) {
          return
        }
        view.dispatch(view.state.tr.insert(selection.$from.pos, tableNode))
        return
      }

      case ItemKey.DELETE: {
        const selection = view.state.selection
        let tableNode = selection.$from.nodeAfter
        if (tableNode == null || tableNode.type !== view.state.schema.nodes.table) {
          return
        }
        view.dispatch(view.state.tr.delete(selection.$from.pos, selection.$from.pos + tableNode.nodeSize))
        return
      }
    }
  }

  const cellFormattingSection = (): SectionElement<any> => {
    return (
      <Section>
        <Item key="alignment" textValue="Alignment">
          <>
            <TableAlignmentTop />
            Alignment
            {() => alignment}
          </>
        </Item>
        <Item key="cellColor" textValue="Cell color">
          <>
            <CellColorIcon view={view} />
            Cell color
            {() => <TableCellColorPicker includeDropdownStyles={false} view={view} focusView={false} />}
          </>
        </Item>
        <Item key="textColor" textValue="Text color">
          <>
            <div>
              <TextColor />
              <ActiveColorsIndicator colors={activeColors} />
            </div>
            Text color
            {() => <TextColorPicker activeColors={activeColors} view={view} />}
          </>
        </Item>
        <Item key="border" textValue="Border">
          <>
            <TableBorder />
            Border
            {() => <BorderOptions view={view} />}
          </>
        </Item>
      </Section>
    )
  }

  const tableFormattingSection = (): SectionElement<any> => {
    const items: ItemElement<any>[] = []
    if (tableSelection) {
      items.push(
        /* TODO
          <Item key="header">
            <TableHeaderRow />
            Header
            {() => (
              <Menu>
                <Item key="header-column">
                  <TableHeaderColumn />
                  Header column
                </Item>
                <Item key="header-row">
                  <TableHeaderRow />
                  Header row
                </Item>
              </Menu>
            )}
          </Item>
        */

        /* TODO
          <Item key="sortRows">
            <Sort />
            Sort rows
            {() => (
              <Menu>
                <Item key="sort-asc">
                  <SortOrderAtoZ />
                  Sort A to Z
                </Item>
                <Item key="sort-desc">
                  <SortOrderZtoA />
                  Sort Z to A
                </Item>
              </Menu>
            )}
          </Item>
        */

        <Item key={ItemKey.FIT_TO_PAGE_WIDTH} textValue="Fit to page width">
          <FitToDoc />
          Fit to page width
        </Item>,

        /* TODO
          <Item key="distribute-columns">
            <DistributeColumns />
            Distribute columns
          </Item>
        */
      )
    }
    if (
      cellSelection &&
      (view.state.selection as CellSelection).$includedCells.length > 1 &&
      canMergeCells(view.state.selection as CellSelection) &&
      !trackingChanges
    ) {
      items.push(
        <Item key={ItemKey.MERGE_CELLS}>
          <CellMerge />
          Merge cells
        </Item>,
      )
    }
    return <Section>{items}</Section>
  }

  const insertSection = (): SectionElement<any> => {
    return (
      <Section>
        <Item key={ItemKey.INSERT} textValue="Insert">
          <>
            <Insert />
            Insert
            {() => (
              <Menu onAction={onAction}>
                <Item key={ItemKey.INSERT_COL_LEFT} textValue="Insert column left">
                  <ColumnLeft />
                  Insert column left
                </Item>
                <Item key={ItemKey.INSERT_COL_RIGHT} textValue="Insert column right">
                  <ColumnRight />
                  Insert column right
                </Item>
                <Item key={ItemKey.INSERT_ROW_BELOW} textValue="Insert row below">
                  <RowBelow />
                  Insert row below
                </Item>
                <Item key={ItemKey.INSERT_ROW_ABOVE} textValue="Insert row above">
                  <RowAbove />
                  Insert row above
                </Item>
              </Menu>
            )}
          </>
        </Item>
      </Section>
    )
  }

  const deleteSection = (): SectionElement<any> => {
    const items: ItemElement<any>[] = [
      <Item key={ItemKey.CLEAR_FORMATTING} textValue="Clear formatting">
        <ClearFormatting />
        Clear formatting
      </Item>,
      <Item key={ItemKey.CLEAR_CONTENTS} textValue="Clear contents">
        <Disapprove />
        Clear contents
      </Item>,
    ]
    if (tableSelection) {
      items.push(
        <Item key={ItemKey.DUPLICATE} textValue="Duplicate">
          <Copy />
          Duplicate
        </Item>,
        <Item key={ItemKey.DELETE} textValue="Delete">
          <Delete />
          Delete
        </Item>,
      )
    }
    return <Section>{items}</Section>
  }

  const sections: SectionElement<any>[] = [
    cellFormattingSection(),
    tableFormattingSection(),
    insertSection(),
    deleteSection(),
  ]

  /* TODO
    <Section>
      <Item key="comment">
        <Comment />
        Comment
      </Item>
      <Item key="task">
        <AddTask />
        Task
      </Item>
    </Section>
  */

  return (
    <Menu aria-label="Menu" onAction={onAction}>
      {sections.filter(({ props: { children } }) => Array.isArray(children) && children.length > 0)}
    </Menu>
  )
}

export function TableContextMenu({
  view,
  trackingChanges,
  onClose,
}: {
  view: EditorView
  trackingChanges: boolean
  onClose?: () => void
}) {
  const menu = useTableContextMenu(view, trackingChanges, onClose)
  return menu
}
